import { refreshAccessToken } from '@/features/auth/utils/tokenRefresh'
import * as Sentry from '@sentry/react'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

// Custom retry logic that handles 401 errors
const shouldRetry = (failureCount: number, error: unknown) => {
  // Don't retry more than once
  if (failureCount > 1) return false

  // Retry 401 errors (will attempt token refresh)
  if (error instanceof AxiosError && error.response?.status === 401) {
    return true
  }

  // Retry network errors
  if (error instanceof AxiosError && !error.response) {
    return true
  }

  return false
}

const queryCache = new QueryCache({
  onError: async (error, query) => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      try {
        await refreshAccessToken()
      } catch (refreshError) {
        console.error('Token refresh failed in query:', refreshError)

        Sentry.captureException(refreshError, {
          tags: {
            feature: 'react_query',
            flow: 'token_refresh_failed',
            type: 'query',
          },
          extra: {
            queryKey: query.queryKey,
          },
        })
      }
    } else {
      Sentry.captureException(error, {
        tags: {
          feature: 'react_query',
          flow: 'query_error',
          type: 'query',
        },
        extra: {
          queryKey: query.queryKey,
          status: error instanceof AxiosError ? error.response?.status : undefined,
        },
      })
    }
  },
})

const mutationCache = new MutationCache({
  onError: async (error, _variables, _context, mutation) => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      try {
        await refreshAccessToken()
      } catch (refreshError) {
        console.error('Token refresh failed in mutation:', refreshError)

        Sentry.captureException(refreshError, {
          tags: {
            feature: 'react_query',
            flow: 'token_refresh_failed',
            type: 'mutation',
          },
          extra: {
            mutationKey: mutation.options.mutationKey,
          },
        })
      }
    } else {
      Sentry.captureException(error, {
        tags: {
          feature: 'react_query',
          flow: 'mutation_error',
          type: 'mutation',
        },
        extra: {
          mutationKey: mutation.options.mutationKey,
          status: error instanceof AxiosError ? error.response?.status : undefined,
        },
      })
    }
  },
})

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    },
    mutations: {
      retry: 0, // Mutations shouldn't auto-retry by default
    },
  },
})
